import React, { useCallback, useRef, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import Joi from '@hapi/joi';
import { Formik } from 'formik';
import { toast } from 'react-toastify';

import SetNewPasswordView from './SetNewPasswordView';

import { useMutation, gql } from '@apollo/client';
import { logger } from 'RootShared/utility/ErrorLogger';
import useDocumentTitle from 'Shared/hooks/useDocumentTitle';

const mutationVerifyResetPasswordToken = gql`
  mutation SetNewPasswordFormVerifyTokenMutation($token: String!, $uidb64: String!) @api(name: root) {
    verifyResetPasswordToken(token: $token, uidb64: $uidb64) {
      result
    }
  }
`;

const mutationSetNewPassword = gql`
  mutation SetNewPasswordFormSetPasswordMutation($password: String!, $token: String!, $uidb64: String!)
    @api(name: root) {
    applyResetPassword(password: $password, token: $token, uidb64: $uidb64) {
      result
    }
  }
`;

const setNewPasswordValidationSchema = Joi.object().keys({
  password: Joi.string()
    .error(() => 'Password cannot be empty')
    .required(),
  confirmPassword: Joi.string()
    .valid(Joi.ref('password'))
    .required()
    .error(() => 'Passwords are not equal'),
});

const SetNewPasswordForm = () => {
  const userType = window.localStorage.getItem('userType') || 'employee';

  useDocumentTitle('SolidERp');

  const [verifyResetPasswordToken] = useMutation(mutationVerifyResetPasswordToken, {
    onCompleted(data) {
      if (!data.verifyResetPasswordToken.result) {
        history.replace(`/login/${userType}/error/`);
      }
    },
    onError(error) {
      logger.error(error);
      history.replace(`/login/${userType}/error/`);
    },
  });

  const [setNewPassword] = useMutation(mutationSetNewPassword, {
    onCompleted(data) {
      onSetNewPasswordSuccess(data);
    },
    onError(error) {
      onSetNewPasswordError(error);
    },
  });

  const { uuid = '', resetToken = '' } = useParams();
  const history = useHistory();

  const initialValues = {
    password: '',
    confirmPassword: '',
  };

  const toastId = useRef(null);

  useEffect(() => {
    if (uuid && resetToken) {
      verifyResetPasswordToken({
        variables: {
          token: resetToken,
          uidb64: uuid,
        },
      });
    }
  }, [resetToken, uuid, verifyResetPasswordToken]);

  const showNotification = useCallback(
    () =>
      (toastId.current = toast('In progress, please wait...', {
        autoClose: true,
        closeButton: false,
        hideProgressBar: true,
      })),
    []
  );

  const onSetNewPasswordSuccess = useCallback(
    (data) => {
      if (!data.errors && data.applyResetPassword.result) {
        toast.update(toastId.current, {
          render: 'Your password has been successfully changed!',
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
          closeButton: true,
          hideProgressBar: false,
        });
        history.replace('/login/employee/');
      } else {
        let errorMessage = 'Password setting error, try again';

        data.errors &&
          data.errors.forEach((error) => {
            if (error.message.toLowerCase().includes('101')) {
              errorMessage = 'Your token is invalid or expired';
            }
          });

        toast.update(toastId.current, {
          render: errorMessage,
          type: toast.TYPE.ERROR,
          autoClose: 3000,
          closeButton: true,
          hideProgressBar: false,
        });

        if (errorMessage.includes('token')) {
          history.replace('/login/employee/');
        }
      }
    },
    [toastId, history]
  );

  const onSetNewPasswordError = useCallback(
    (error) => {
      logger.error(error);
      toast.update(toastId.current, {
        render: (error && error.message) || 'Email sending error',
        type: toast.TYPE.ERROR,
        autoClose: 3000,
        closeButton: true,
        hideProgressBar: false,
      });
      return error;
    },
    [toastId]
  );

  const handleSetNewPasswordSubmit = useCallback(
    (values, { setSubmitting, setValues }) => {
      showNotification();

      setNewPassword({
        variables: {
          token: resetToken,
          uidb64: uuid,
          password: values.password,
        },
      });

      setSubmitting(false);
      setValues(initialValues);
    },
    [setNewPassword, initialValues, showNotification, resetToken, uuid]
  );

  const handleSetNewPasswordValidation = useCallback(async (values) => {
    const validationResult = await Joi.validate(
      {
        ...values,
      },
      setNewPasswordValidationSchema,
      {
        abortEarly: false,
      }
    );

    return validationResult.error;
  }, []);

  const renderFunction = useCallback((formikProps) => <SetNewPasswordView formikProps={formikProps} />, []);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSetNewPasswordSubmit}
      validate={handleSetNewPasswordValidation}
      validateOnChange={false}
      validateOnBlur={true}
      render={renderFunction}
    />
  );
};

export default SetNewPasswordForm;
